/**
 * Leçons et exercices des livres scolaires (dont suppression en lot,
 * rattrapage des durées YouTube, exercices sans vidéo).
 *
 * Extrait de l'ancien services/api.ts (Lot 8, déplacement pur).
 */

import {
  BookLesson,
  BookExercise,
  BookExercisesWithoutVideoResponse,
} from '../types';
import type { ApiResponse, DataResponse } from '../types/api';
import { ADMIN_URLS } from '../config/adminApi';
import { createApiClient, HTTP_TIMEOUT, presentParams, withEnv } from '../lib/http';
import { AppEnvironment } from '../config/environment';

const api = createApiClient();

/** Résultat d'une suppression par lot d'exercices de livre. */
interface BatchDeleteResult {
  deleted: number;
  deletedIds: string[];
  failed: { id: string; error: string }[];
}

/** Résultat du recalcul des durées vidéo des exercices de livre. */
export interface BackfillDurationsResult {
  success: boolean;
  env: AppEnvironment;
  total: number;
  updated: number;
  skipped: number;
  failed: number;
  details: { id: string; status: 'updated' | 'skipped' | 'failed'; duration?: number; reason?: string }[];
}

export class BookContentApiService {
  /** Liste les leçons de livre de l'environnement. */
  static async getBookLessons(env: AppEnvironment, chapterId?: string): Promise<BookLesson[]> {
    const params = presentParams({ chapterId });
    const response = await api.get<DataResponse<BookLesson[]>>('/book_lessons', withEnv(env, { params }));
    return response.data.data;
  }

  /** Lit une leçon de livre par son identifiant. */
  static async getBookLessonById(env: AppEnvironment, id: string): Promise<BookLesson> {
    const response = await api.get<DataResponse<BookLesson>>(`/book_lessons/${id}`, withEnv(env));
    return response.data.data;
  }

  /** Crée une leçon de livre. */
  static async createBookLesson(env: AppEnvironment, lesson: Omit<BookLesson, 'id'>): Promise<ApiResponse<BookLesson>> {
    const response = await api.post<ApiResponse<BookLesson>>(`/book_lessons`, lesson, withEnv(env));
    return response.data;
  }

  /** Met à jour une leçon de livre. */
  static async updateBookLesson(env: AppEnvironment, id: string, lesson: Partial<BookLesson>): Promise<ApiResponse<BookLesson>> {
    const response = await api.put<ApiResponse<BookLesson>>(`/book_lessons/${id}`, lesson, withEnv(env));
    return response.data;
  }

  /** Supprime une leçon de livre. */
  static async deleteBookLesson(env: AppEnvironment, id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.content}/book_lessons/${id}`, withEnv(env));
    return response.data;
  }

  /** Liste les exercices de livre de l'environnement. */
  static async getBookExercises(env: AppEnvironment, lessonId?: string): Promise<BookExercise[]> {
    const params = presentParams({ lessonId });
    const response = await api.get<DataResponse<BookExercise[]>>('/book_exercises', withEnv(env, { params }));
    return response.data.data;
  }

  /** Lit un exercice de livre par son identifiant. */
  static async getBookExerciseById(env: AppEnvironment, id: string): Promise<BookExercise> {
    const response = await api.get<DataResponse<BookExercise>>(`/book_exercises/${id}`, withEnv(env));
    return response.data.data;
  }

  /** Crée un exercice de livre. */
  static async createBookExercise(env: AppEnvironment, exercise: Omit<BookExercise, 'id'>): Promise<ApiResponse<BookExercise>> {
    const response = await api.post<ApiResponse<BookExercise>>(`/book_exercises`, exercise, withEnv(env));
    return response.data;
  }

  /** Met à jour un exercice de livre. */
  static async updateBookExercise(env: AppEnvironment, id: string, exercise: Partial<BookExercise>): Promise<ApiResponse<BookExercise>> {
    const response = await api.put<ApiResponse<BookExercise>>(`/book_exercises/${id}`, exercise, withEnv(env));
    return response.data;
  }

  /** Supprime un exercice de livre. */
  static async deleteBookExercise(env: AppEnvironment, id: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.content}/book_exercises/${id}`, withEnv(env));
    return response.data;
  }

  /**
   * Supprime plusieurs BookExercise en batch (max 50)
   */
  static async batchDeleteBookExercises(
    env: AppEnvironment,
    ids: string[]
  ): Promise<ApiResponse<BatchDeleteResult>> {
    // `env` dans le CORPS : la route lit `body_env(data, 'env', 'test')`
    // (admin_api/content/crud_routes.py), l'en-tête n'y suffit pas.
    const response = await api.post<ApiResponse<BatchDeleteResult>>('/book_exercises/batch-delete', { ids, env }, {
      timeout: HTTP_TIMEOUT.EXTENDED
    });
    return response.data;
  }

  /**
   * Met à jour en lot la durée (videoDuration) des book_exercises en interrogeant
   * la vraie durée de la vidéo YouTube. ids vide → tous les exercices de l'env.
   */
  static async backfillBookExerciseDurations(
    env: AppEnvironment,
    ids: string[],
    overwrite: boolean = false
  ): Promise<BackfillDurationsResult> {
    const response = await api.post<BackfillDurationsResult>(
      `${ADMIN_URLS.videoProjects}/book-exercises/backfill-durations`,
      { ids, overwrite },
      withEnv(env, { timeout: HTTP_TIMEOUT.BATCH })
    );
    return response.data;
  }

  /**
   * Liste les exercices sans vidéo (url vide/absent), enrichis avec leur chaîne hiérarchique.
   * Utilisé pour les compteurs contextuels dans les filtres de la page /book-exercises.
   */
  static async getBookExercisesWithoutVideo(
    env: AppEnvironment
  ): Promise<BookExercisesWithoutVideoResponse> {
    const response = await api.get<BookExercisesWithoutVideoResponse>(`/book_exercises/without-video`, withEnv(env));
    return response.data;
  }
}

export default BookContentApiService;
